import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";

function section(title, ...steps) {
  return { title, steps };
}

const HELP_CONTENT = {
  "/app/dashboard": {
    title: "Dashboard",
    intro: "La Dashboard è il quadro di controllo a 360 gradi della struttura. Riunisce dati reali di reception, camere, prenotazioni, soggiorni, documenti fiscali, incassi, IVA, preventivi, clienti e servizi, separando chiaramente situazione attuale, mese corrente e previsioni.",
    sections: [
      section("Filtrare e aggiornare la Dashboard",
        "Usa Sede per vedere tutta l'azienda oppure limitare gli indicatori a una struttura specifica.",
        "La Dashboard si aggiorna automaticamente ogni 60 secondi quando la pagina è visibile. Usa Aggiorna quando vuoi forzare immediatamente il ricalcolo.",
        "Controlla Ultimo aggiornamento per sapere a quale momento si riferiscono i dati mostrati.",
        "I collegamenti contestuali presenti nelle singole schede della Dashboard aprono direttamente il dettaglio pertinente quando serve approfondire un indicatore."),
      section("Leggere i KPI principali",
        "Occupazione ora utilizza le camere realmente In House e tiene conto delle camere indisponibili per manutenzione o fuori servizio.",
        "Arrivi e Partenze mostrano i movimenti della giornata, mentre In House mostra soggiorni e ospiti effettivamente presenti.",
        "Fatturato mese usa esclusivamente documenti fiscali attivi e considera Note di Credito e storni con segno negativo.",
        "Incassato mese usa gli importi realmente pagati sui documenti; Da incassare evidenzia eventuali saldi residui.",
        "ADR misura il ricavo camera medio per notte venduta, mentre RevPAR rapporta il ricavo camere alle notti camera effettivamente disponibili nel mese."),
      section("Controllare criticità e operatività",
        "Da controllare raccoglie automaticamente camere da pulire o controllare, manutenzioni, preventivi scaduti o in scadenza, saldi fiscali residui, recapiti mancanti e giornate ad alta occupazione.",
        "Premi una criticità per raggiungere direttamente la finestra da cui puoi intervenire sul dato reale.",
        "Occupazione prossimi 7 giorni mostra camere occupate e disponibili, arrivi, partenze e percentuale prevista per ogni giorno.",
        "Prossimi movimenti riunisce gli arrivi e le partenze imminenti con cliente, camera, data e stato della prenotazione."),
      section("Leggere situazione economica e fiscale",
        "Situazione economica del mese separa Imponibile, IVA, Lordo, Incassato, Da incassare, Ticket medio, documenti attivi e documenti annullati.",
        "Ultimi 7 giorni mostra per ogni giornata il numero di documenti, il lordo fiscale e quanto risulta incassato.",
        "Mix fiscale distingue Fatture, Ricevute, Note di Credito e Storni e indica anche quante fatture libere sono state emesse.",
        "Castelletto IVA raggruppa imponibile e imposta per aliquota usando le righe reali dei documenti fiscali emessi."),
      section("Clienti, preventivi, servizi e camere",
        "Clienti mostra il patrimonio anagrafiche, aziende/Ditte Individuali/PA, VIP, clienti bloccati e quanti record non hanno email o telefono.",
        "Preventivi mostra quanti sono ancora aperti, il loro valore complessivo e quanti sono scaduti o prossimi alla scadenza.",
        "Servizi più addebitati nel mese classifica gli extra per valore e quantità usando gli addebiti reali dei soggiorni.",
        "Housekeeping riepiloga camere pulite, occupate, da pulire, da controllare e indisponibili."),
      section("Uso consigliato a inizio turno",
        "Controlla prima Da controllare, Occupazione ora, Arrivi oggi e Partenze oggi.",
        "Verifica poi Occupazione prossimi 7 giorni per anticipare giornate piene, manutenzioni o carenza di camere.",
        "Controlla saldi da incassare, preventivi scaduti e recapiti fiscali incompleti prima di chiudere il turno amministrativo.",
        "Usa Stampe, Statistiche e Analisi quando devi approfondire un indicatore: la Dashboard riassume, le finestre analitiche spiegano il dettaglio."),
    ],
  },
  "/app/bookings": {
    title: "Planning",
    intro: "Il Planning permette di vedere e gestire le prenotazioni sulle camere, creare nuove prenotazioni, modificare quelle esistenti, gestire ospiti e pagamenti, eseguire check in e Fast Check Out e controllare disponibilità e manutenzioni.",
    sections: [
      section("Leggere e spostarsi nel Planning",
        "Apri Planning dalla sidebar e attendi il caricamento delle camere e delle prenotazioni.",
        "Le camere sono sulle righe e i giorni sulle colonne. Ogni blocco colorato rappresenta una prenotazione nel periodo occupato.",
        "Usa Oggi per tornare immediatamente alla data corrente e i controlli temporali per cambiare periodo o visualizzazione.",
        "Usa i filtri disponibili per limitare il planning per tipologia camera e stato prenotazione senza modificare nessun dato.",
        "Controlla colore, stato, camera e durata del blocco prima di aprire una prenotazione."),
      section("Espandere il Planning a tutto schermo",
        "Premi Espandi Tutto a destra del pulsante Oggi.",
        "La vista a tutto schermo conserva i totali del planning, la barra con Oggi e Trimestre e i filtri correnti.",
        "Usala quando devi confrontare molte camere o un periodo ampio senza lo spazio occupato dal resto dell'applicazione.",
        "Per uscire usa il pulsante Chiudi in alto. Tornando alla vista normale mantieni il contesto di lavoro corrente."),
      section("Creare una nuova prenotazione",
        "Individua una cella libera della camera e della data desiderata e apri il menu sull'area libera, oppure usa l'azione Nuova Prenotazione disponibile nella finestra.",
        "Compila il Cliente Ospite scegliendo un'anagrafica esistente oppure creandone una nuova con il pulsante dedicato.",
        "Verifica Intestatario Fattura: se Cliente e Intestatario Fattura sono uguali mantieni il relativo check; se lo togli, seleziona o crea l'intestatario corretto nella form separata.",
        "Compila camera, check in, check out, stato prenotazione, numero adulti, bambini e animali e gli altri dati richiesti.",
        "Aggiungi eventuali Ospiti Correlati e completa per i minori i dati dell'accompagnatore o tutore quando richiesti.",
        "Compila la sezione Pagamenti con metodo, note, importo e check Acconto per ogni pagamento già ricevuto.",
        "Completa note e dati aggiuntivi, poi premi Salva e Chiudi. Se un campo obbligatorio è errato il relativo errore viene evidenziato e il pallino della sezione diventa rosso."),
      section("Modificare una prenotazione esistente",
        "Fai clic con il tasto destro sul blocco della prenotazione e scegli Prenotazione / Soggiorno.",
        "Controlla prima cliente, intestatario fattura, camera, date e stato, poi modifica solo ciò che serve.",
        "Se cambi camera o date, verifica che il nuovo intervallo sia realmente libero nel planning.",
        "Nella sezione Pagamenti puoi vedere i pagamenti già registrati, aggiungerne altri, modificarli o rimuoverli.",
        "Il check Acconto identifica i pagamenti anticipati e mantiene coerenti i dati utilizzati nei documenti di acconto e nel conto del soggiorno.",
        "Premi Salva e Chiudi e verifica subito che blocco, posizione e stato siano aggiornati nel planning.",
        "Le prenotazioni Concluse e già fatturate non possono essere eliminate: la voce Elimina rimane disattivata e il tooltip spiega che il soggiorno è fatturato."),
      section("Menu rapido su prenotazione",
        "Fai clic con il tasto destro su una prenotazione per aprire le azioni disponibili per quello specifico stato.",
        "Usa Prenotazione / Soggiorno per aprire lo stesso overlay Soggiorno e lavorare sulle tab Prenotazione, Servizi e Check Out senza chiudere e riaprire finestre diverse.",
        "Usa Anagrafica Ospite per aprire o aggiornare rapidamente il cliente collegato quando l'azione è disponibile.",
        "Usa Conferma Prenotazione quando devi passare una prenotazione allo stato confermato.",
        "Usa Fast Check In per portare direttamente la prenotazione In House dopo aver verificato i dati necessari.",
        "Usa Fast Check Out per aprire direttamente la tab Check Out dello stesso overlay Soggiorno, mantenendo disponibili anche Prenotazione e Servizi."),
      section("Controllare disponibilità e manutenzioni",
        "Apri il menu su uno spazio libero per usare la funzione di disponibilità quando presente.",
        "Controlla sempre che la camera non sia occupata o interessata da una manutenzione nel periodo scelto.",
        "Dal menu della camera usa Modifica Camera per correggere l'anagrafica della stanza senza lasciare il planning.",
        "Usa la gestione Manutenzione per creare o modificare un periodo di indisponibilità della camera.",
        "Dopo ogni intervento verifica visivamente il planning per assicurarti che disponibilità e blocchi siano coerenti."),
      section("Fast Check Out dal Planning",
        "Apri Fast Check Out sulla prenotazione interessata. L'overlay è lo stesso usato dal Check Out di Soggiorni.",
        "Controlla Dati Riassuntivi, tipo documento, numerazione, data emissione, sconti e arrotondamento.",
        "Nella tab Check Out ritrovi i pagamenti registrati nella prenotazione, con metodo, note, importo e tipologia del pagamento.",
        "I pagamenti già registrati vengono caricati automaticamente e puoi correggerli o aggiungerne altri prima della chiusura.",
        "Controlla anteprima, Totale Imponibile, Imposta, Totale Fattura, sconti, arrotondamento e pagamenti prima di emettere il documento fiscale.",
        "Usa Concludi Soggiorno - Emetti Documento per chiudere il soggiorno ed emettere il documento; Salva e Chiudi salva invece le modifiche senza sostituire l’azione fiscale dedicata."),
    ],
  },
  "/app/booking-accounts": {
    title: "Soggiorni",
    intro: "Soggiorni raccoglie prenotazione, servizi addebitati e check out nello stesso overlay Soggiorno. Le tre tab Prenotazione, Servizi e Check Out restano disponibili senza chiudere la finestra e mantengono dati e totali sincronizzati.",
    sections: [
      section("Trovare il soggiorno corretto",
        "Apri Soggiorni dalla sidebar e attendi il caricamento dei conti prenotazione.",
        "Usa periodo, ricerca rapida e filtri per trovare il soggiorno tramite ospite, camera, date, stato o altri dati disponibili.",
        "I filtri a imbuto delle colonne permettono di restringere ulteriormente la tabella. Usa l'azzeramento dei filtri quando vuoi tornare alla vista completa.",
        "Prima di usare un'azione verifica sempre ospite, camera e periodo della riga selezionata."),
      section("Leggere Servizi Addebitati e totali",
        "Nella colonna Servizi Addebitati ogni descrizione resta allineata a sinistra e il relativo importo è allineato al margine destro della stessa colonna.",
        "Non usare il testo come separatore: descrizione e importo sono due informazioni distinte nella stessa riga.",
        "Controlla Totale Prenotazione, Totale Servizi e Totale Fattura per capire come è composto il conto.",
        "Apri Servizi quando devi vedere o modificare il dettaglio di un addebito invece di dedurre il valore dal solo riepilogo."),
      section("Usare l'overlay Soggiorno",
        "Apri il soggiorno dalla colonna Azioni oppure dal Planning: viene usato lo stesso overlay Soggiorno.",
        "Usa i pulsanti fissi Prenotazione, Servizi e Check Out sotto la topbar dell'overlay per cambiare area senza chiudere e riaprire la finestra.",
        "Le modifiche effettuate in una tab aggiornano i dati e i totali utilizzati dalle altre tab.",
        "Se il soggiorno è Concluso e fatturato, Elimina rimane disattivato e il tooltip indica che la prenotazione non può essere eliminata perché fatturata."),
      section("Gestire i Servizi Addebitati",
        "Apri la tab Servizi dell’overlay Soggiorno dalla riga del soggiorno o dai pulsanti superiori dell’overlay.",
        "La prima sezione mostra i dati riassuntivi della prenotazione e le date del soggiorno.",
        "Nella sezione Servizi Addebitati il soggiorno automatico e i servizi manuali usano gli stessi campi: Tipo Servizio, Data Addebito, Tipo Addebito, Prezzo, Quantità, % Sconto, % Aliquota IVA e Origine Inserimento.",
        "Origine Inserimento è informativa e può indicare Automatico, Manuale da Desktop, Manuale da Mobile o Default.",
        "Usa il pulsante + identico alle altre liste overlay per aggiungere un servizio e la X per eliminare una riga aggiunta o predisporre la rimozione di una riga esistente.",
        "Espandi una riga quando devi vedere i campi aggiuntivi. Per gli addebiti ad Accumulo apri il dettaglio e gestisci le singole voci accumulate.",
        "Controlla i valori Imponibile, Imposta, Totale e Ivato prima di salvare."),
      section("Check Out e Pagamenti",
        "Apri la tab Check Out oppure usa Fast Check Out: entrambi portano alla stessa tab dello stesso overlay Soggiorno.",
        "Controlla Dati Riassuntivi e tutti i valori dell'emissione fiscale prima di procedere.",
        "Prima di Dati Aggiuntivi trovi la sezione Pagamenti, condivisa con la tab Prenotazione dello stesso overlay Soggiorno.",
        "I pagamenti già esistenti vengono caricati automaticamente. Per ogni riga puoi selezionare metodo, inserire note, importo e indicare se è un Acconto.",
        "Aggiungi o correggi i pagamenti prima dell’emissione. Gli acconti restano gestiti nella sezione Pagamenti e nei relativi dati documento, mentre i totali fiscali finali mostrano Imponibile, Imposta e Totale Fattura.",
        "Se una riga pagamento contiene dati ma manca il metodo di pagamento, completa la riga prima di salvare."),
      section("Emissione del documento fiscale",
        "Scegli Fattura o Ricevuta e verifica la modalità di numerazione Automatica o Manuale.",
        "In Automatico il numero è proposto e gestito dal progressivo corretto per tipo documento e anno; l'anteprima non consuma numeri.",
        "In Manuale puoi modificare il numero proposto usando esclusivamente cifre. Il sistema blocca duplicati e incoerenze cronologiche.",
        "La Data Emissione non può essere futura e deve essere coerente con la numerazione già emessa.",
        "Usa Sconto Incondizionato per importo o percentuale e il campo Totale Fattura per ottenere l'eventuale Abbuono Attivo o Passivo tramite arrotondamento.",
        "Controlla Totale Imponibile, Imposta e Totale Fattura. Sconti e arrotondamento incidono sui valori finali secondo le regole mostrate nella tab Check Out."),
      section("Anteprima, orientamento, stampa ed esportazione",
        "Apri Anteprima Stampa dopo aver completato il conto e i dati fiscali.",
        "Il report mostra intestazione azienda, sede legale, dati documento, cliente/intestatario fattura, soggiorno/ospite, righe addebitate, castelletto IVA e totali.",
        "Usa il comando Verticale/Orizzontale per cambiare orientamento. In orizzontale i blocchi finali vengono distribuiti sullo spazio disponibile.",
        "Prezzo e totali riga sono allineati a destra, Quantità è centrata e i totali finali rispettano lo stesso allineamento.",
        "Stampa ed Esporta/Download devono utilizzare lo stesso report che vedi nell'anteprima, senza generare una seconda impaginazione diversa.",
        "Se trovi un errore, chiudi l'anteprima e correggi il dato nella finestra di origine prima di emettere."),
      section("Riaprire, annullare e mantenere lo storico fiscale",
        "Quando il documento è emesso, Tipo Documento, Numero Documento, Data Documento e Note mostrano i dati realmente utilizzati nell’ultima emissione.",
        "Riapri Soggiorno - Annulla Documento conserva il documento annullato nello storico e riporta il soggiorno allo stato operativo previsto.",
        "Dopo la riapertura VestaMNG ripropone il tipo documento, la numerazione Manuale, il numero dell’ultima chiusura, la data documento e le note dell’ultimo documento annullato, così non devi annotarli esternamente.",
        "Se il documento annullato era l’ultimo progressivo automatico del registro, il contatore può retrocedere; un documento annullato non deve bloccare il riutilizzo manuale di un numero non più fiscalmente attivo.",
        "Anche documenti di acconto e relativi storni mantengono i dati storici necessari quando vengono annullati o riaperti secondo le azioni disponibili."),
    ],
  },
  "/app/quotes": {
    title: "Preventivi",
    intro: "Preventivi permette di creare offerte economiche indipendenti dalle prenotazioni, con cliente, sede, righe, quantità, prezzi, sconti, IVA, note, validità e stampa. I preventivi aperti prossimi alla scadenza vengono richiamati anche nelle Notifiche.",
    sections: [
      section("Trovare e filtrare i preventivi",
        "Usa la ricerca rapida per numero preventivo, cliente, email o note.",
        "Usa i filtri a imbuto sulle intestazioni per restringere l’elenco per data, validità, stato, cliente e importo.",
        "Azzera ricerca e filtri dal menu Opzioni quando vuoi tornare all’elenco completo.",
        "Controlla sempre numero, intestatario, data emissione, validità e stato prima di modificare o stampare."),
      section("Creare un nuovo preventivo",
        "Apri Opzioni e scegli Nuovo Preventivo.",
        "Seleziona una sede e, quando disponibile, un cliente esistente; in alternativa compila liberamente i dati dell’intestatario.",
        "Inserisci data, eventuale validità e almeno una riga con descrizione, quantità, prezzo, sconto e aliquota IVA.",
        "Controlla imponibile, imposta e totale calcolati dalle righe e aggiungi eventuali note.",
        "Salva soltanto dopo aver verificato che recapiti e condizioni riportate nel preventivo siano corretti."),
      section("Modificare, chiudere e stampare",
        "Premi Modifica sulla riga interessata per riaprire i dati del preventivo.",
        "Lo stato Bozza indica un preventivo ancora operativo; usa lo stato Chiuso quando non deve più essere considerato tra quelli aperti.",
        "La data di validità alimenta gli avvisi nelle Notifiche quando un preventivo aperto è in scadenza o è già scaduto.",
        "Premi Stampa per produrre il documento del preventivo dopo aver verificato righe, IVA, totale e note."),
      section("Lavorare con le Notifiche",
        "Le Notifiche possono segnalare preventivi in scadenza e preventivi scaduti ancora in stato Bozza.",
        "Apri l’avviso per raggiungere Preventivi e controlla il documento interessato.",
        "Dopo aver aggiornato validità o stato, l’avviso operativo si riallinea automaticamente al dato reale."),
    ],
  },
  "/app/free-invoices": {
    title: "Fatture",
    intro: "Fatture permette di emettere fatture libere non collegate a una prenotazione usando lo stesso registro fiscale delle fatture dei soggiorni. Da qui puoi creare il documento, visualizzarlo, stamparlo, generare XML e condividerlo tramite Mail, WhatsApp o Messaggio.",
    sections: [
      section("Trovare e filtrare le fatture libere",
        "Usa ricerca rapida e filtri a imbuto per numero documento, cliente, data, sede, stato e totale.",
        "Le intestazioni usano lo stesso stile e gli stessi filtri delle altre finestre gestionali.",
        "Azzera ricerca e filtri dal menu Opzioni quando vuoi ripartire dall’elenco completo."),
      section("Emettere una fattura libera",
        "Apri Opzioni e scegli Nuova Fattura Libera.",
        "Seleziona sede e cliente oppure inserisci liberamente l’intestatario previsto.",
        "Aggiungi almeno una riga con descrizione, quantità, prezzo, sconto e aliquota IVA e controlla i totali calcolati.",
        "Scegli numerazione Automatica oppure Manuale. Il registro è condiviso con le fatture dei soggiorni, quindi il sistema protegge da duplicati e salti incoerenti.",
        "Verifica data documento e note prima dell’emissione."),
      section("Visualizzare, stampare e generare XML",
        "Usa Visualizza / Stampa per aprire la rappresentazione del documento fiscale attivo.",
        "Per le fatture usa XML per scaricare il file elettronico del singolo documento.",
        "La Fatturazione amministrativa permette invece di generare l’archivio XML complessivo organizzato per Azienda / Sede / Anno / Mese."),
      section("Condividere il documento",
        "Mail invia il documento all’indirizzo cliente tramite il server SMTP configurato per VestaMNG.",
        "WhatsApp apre la conversazione del numero cliente con un link firmato temporaneo al documento già inserito nel messaggio.",
        "Messaggio apre l’app SMS del dispositivo con lo stesso link temporaneo.",
        "Se manca email o telefono, VestaMNG segnala il recapito mancante e le Notifiche possono ricordare i documenti fiscali recenti senza recapiti completi."),
      section("Annullare una fattura libera",
        "L’annullamento mantiene il documento nello storico ma lo rende fiscalmente non attivo.",
        "Se il documento annullato era l’ultimo numero del registro, il progressivo automatico può tornare indietro secondo le regole fiscali già previste.",
        "Il numero annullato non deve bloccare una successiva emissione manuale quando non rappresenta più un documento attivo."),
    ],
  },
  "/app/invoicing": {
    title: "Fatturazione",
    intro: "Fatturazione è il centro amministrativo dei documenti fiscali attivi. Permette di filtrare per periodo e sede, visualizzare o condividere i documenti, scaricare XML singoli e creare l’archivio XML delle fatture organizzato per Azienda / Sede / Anno / Mese.",
    sections: [
      section("Filtrare i documenti fiscali",
        "Usa Dal e Al per limitare il periodo e Sede per lavorare su una struttura specifica.",
        "Usa ricerca rapida e filtri a imbuto sulle colonne per numero, tipo documento, cliente, data, sede, camera e totale.",
        "Il riepilogo sopra la tabella mostra quanti documenti fiscali attivi sono presenti e quante fatture da prenotazione sono disponibili per l’XML.",
        "Azzera ricerca e filtri dal menu Opzioni per ripristinare la vista completa."),
      section("Gestire il singolo documento",
        "Usa Visualizza / Stampa per aprire il documento fiscale senza modificarne i dati.",
        "Per una Fattura usa XML per generare e scaricare il file elettronico del singolo documento.",
        "Usa Mail, WhatsApp o Messaggio per condividere il documento con i recapiti memorizzati sul cliente.",
        "I link condivisi sono firmati e temporanei: non rendono il documento pubblicamente disponibile in modo permanente."),
      section("Scaricare l’archivio XML",
        "Apri Opzioni e scegli Scarica Archivio XML dopo aver impostato eventuali filtri Dal, Al e Sede.",
        "L’archivio contiene le fatture da prenotazione disponibili nel periodo selezionato.",
        "La struttura logica segue Azienda / Sede / Anno / Mese, così una sola gestione aziendale mantiene separata l’operatività delle diverse sedi.",
        "Il download è adatto all’archiviazione e alla successiva integrazione con storage persistente o invio SdI."),
      section("Recapiti e Notifiche",
        "Per Mail serve un indirizzo cliente valido e la configurazione SMTP del backend.",
        "Per WhatsApp e Messaggio serve un numero cliente utilizzabile dal dispositivo.",
        "Le Notifiche segnalano i documenti fiscali recenti a cui manca email, telefono o entrambi, così puoi completare l’anagrafica prima della condivisione."),
    ],
  },
  "/app/search": {
    title: "Ricerca",
    intro: "Ricerca mostra i risultati trovati dalla ricerca globale della Topbar, raggruppati per area, e permette di raggiungere rapidamente il record o la finestra corretta.",
    sections: [
      section("Avviare una ricerca globale",
        "Clicca la ricerca nella Topbar e scrivi un dato significativo, per esempio cognome, ragione sociale, camera, codice, telefono o email.",
        "Attendi il caricamento asincrono dei risultati prima di selezionare una voce.",
        "Usa termini abbastanza specifici da evitare molti record simili.",
        "Premi Invio o apri la pagina Ricerca per vedere i risultati raggruppati per reparto."),
      section("Leggere i risultati",
        "Ogni sezione indica l'area da cui proviene il risultato, per esempio Planning, Soggiorni, Preventivi, Fatture, Fatturazione, Clienti, Camere, Utenti, Tabelle o Sistema.",
        "Leggi descrizione e riferimenti del risultato prima di aprirlo, soprattutto quando esistono nomi simili.",
        "Se non trovi corrispondenze, modifica il termine cercato invece di aprire record approssimativi.",
        "Il risultato selezionato viene portato alla finestra corretta e, nelle griglie supportate comprese Preventivi, Fatture e Fatturazione, viene evidenziato in oro."),
      section("Aprire il record trovato",
        "Clicca una sola volta sul risultato desiderato.",
        "La webapp apre la finestra corretta e, quando previsto, imposta automaticamente ricerca, filtro o record selezionato.",
        "Attendi che il caricamento sia terminato prima di premere Modifica o altre azioni.",
        "Controlla sempre il record aperto prima di salvarlo."),
      section("Risultati di Tabelle e Sistema",
        "La ricerca include anche le finestre della sidebar: puoi cercare termini come XML, WhatsApp, notifiche, preventivo o fattura per raggiungere direttamente la funzione collegata.",
        "Quando un risultato appartiene a Tabelle o Sistema viene aperto il catalogo o la collection corretta.",
        "La ricerca rapida viene valorizzata con il valore scelto e il record viene portato in evidenza.",
        "Il caricamento continua finché il valore cercato è stato trovato, anche quando non appartiene al primo gruppo di righe caricate.",
        "Le azioni disponibili restano comunque limitate dalle abilitazioni del ruolo corrente."),
    ],
  },
  "/app/rooms": {
    title: "Camere",
    intro: "Camere gestisce l'anagrafica delle stanze, la loro sede e tipologia, i posti letto, le dotazioni, lo stato, l'ordine e le operazioni di importazione o esportazione.",
    sections: [
      section("Cercare e leggere le camere",
        "Usa ricerca rapida e filtri di colonna per individuare numero camera, sede, tipologia, piano, stato o altri dati disponibili.",
        "Leggi sempre l'intera riga prima di usare i pulsanti Azioni.",
        "Le colonne modificabili mantengono una larghezza sufficiente a leggere il titolo e possono essere gestite con i filtri a imbuto.",
        "Usa Opzioni per azzerare ricerca e filtri quando vuoi tornare all'elenco completo."),
      section("Creare o modificare una camera",
        "Premi Nuova Camera oppure Modifica sulla riga esistente.",
        "Nella sezione Dati Camera compila i campi richiesti come numero, sede, tipologia, piano, descrizione, stato e gli altri valori presenti nell'overlay.",
        "Controlla che il numero camera non crei duplicati e che la sede e la tipologia selezionate siano corrette.",
        "Se dopo Salva e Chiudi compaiono errori, usa il pallino rosso della relativa sezione per raggiungerla immediatamente."),
      section("Configurare Tipologie Letto",
        "Apri la sezione Tipologie Letto della camera.",
        "Seleziona le icone o le tipologie che rappresentano realmente i posti letto presenti nella stanza.",
        "Controlla quantità e combinazione prima di salvare perché queste informazioni vengono riutilizzate nella gestione operativa.",
        "Quando gli elementi sono molti vengono disposti su più righe senza cambiare il significato della selezione."),
      section("Configurare Dotazioni Disponibili",
        "Apri Dotazioni Disponibili e seleziona soltanto comfort e accessori realmente presenti nella camera.",
        "Usa la stessa logica delle tipologie letto: gli elementi selezionati restano associati alla camera.",
        "Salva e riapri la camera se vuoi verificare che tutte le dotazioni siano state registrate correttamente."),
      section("Ordine, stato, importazione ed esportazione",
        "Usa Attivo/Disattivo per mantenere lo storico di una camera senza proporla quando non deve essere operativa.",
        "Modifica Ordine con frecce o campo numerico solo se il tuo livello di permesso lo consente.",
        "Usa Importa per caricare più camere seguendo il tracciato richiesto e controlla i dati prima della conferma.",
        "Usa Esporta per creare un file con l'elenco corrente e Eliminazione Multipla solo dopo aver verificato attentamente le righe selezionate."),
      section("Manutenzioni dal Planning",
        "Le indisponibilità temporanee della camera vengono gestite dal Planning tramite la funzione Manutenzione.",
        "Imposta il periodo corretto e verifica che non entri in conflitto con prenotazioni già presenti.",
        "Dopo il salvataggio torna sul Planning per controllare visivamente il periodo bloccato."),
    ],
  },
  "/app/customers": {
    title: "Clienti",
    intro: "Clienti gestisce anagrafiche private, ditte individuali, ditte, pubbliche amministrazioni e animali, con dati fiscali, residenza o sede legale, contatti, documenti, privacy, preferenze, esigenze, alert e riferimenti esterni.",
    sections: [
      section("Creare l'anagrafica corretta",
        "Premi Nuovo Cliente e seleziona il Tipo Anagrafica prima di compilare il resto.",
        "Per Privato e Ditta Individuale compila Nome e Cognome quando richiesti; per Ditta, Pubblica Amministrazione e Ditta Individuale completa Ragione Sociale secondo le regole della tipologia.",
        "La Ditta Individuale può costruire automaticamente la Ragione Sociale da Cognome e Nome quando previsto.",
        "I campi non utilizzabili per la tipologia scelta vengono disattivati e visualizzati in grigio, quindi non devono essere compilati manualmente.",
        "I testi descrittivi eliminano gli spazi ai margini quando lasci il campo, senza alterare il testo mentre stai scrivendo."),
      section("Dati fiscali, nazione e SDI",
        "Per anagrafiche italiane rispetta le obbligatorietà di Codice Fiscale, Partita IVA e Codice SDI previste dal tipo cliente.",
        "Per Privato italiano il Codice SDI può essere gestito automaticamente con 0000000; per Pubblica Amministrazione usa il formato previsto a 6 caratteri.",
        "Per clienti esteri il Codice SDI viene gestito con XXXXXXX e il CAP con 00000 quando previsto dalle regole estere.",
        "Per l'estero compila l'Identificativo Fiscale Estero obbligatorio quando richiesto; i valori generici ammessi dipendono dalla casistica fiscale.",
        "Per Ditta, Pubblica Amministrazione o Ditta Individuale italiana puoi usare il pulsante informazioni accanto alla Partita IVA quando il servizio di verifica dati è configurato.",
        "Controlla i messaggi di duplicato prima di salvare: Codice Fiscale e Partita IVA seguono le regole di unicità previste per ciascun tipo anagrafica."),
      section("Dati Residenza / Sede Legale",
        "Compila indirizzo, civico, nazione e Località nella sezione Dati Residenza / Sede Legale.",
        "Quando selezioni una Località italiana il CAP viene proposto dal catalogo dei CAP disponibili; se la località ha più CAP puoi scegliere quello corretto.",
        "Se cambi Località, ricontrolla sempre il CAP perché viene riallineato alla nuova selezione.",
        "Per le anagrafiche estere segui i valori automatici e le regole previste per nazione, CAP e identificativi."),
      section("Gestire i Contatti",
        "Nella sezione Contatti ogni riga contiene tipologia, prefisso quando necessario, valore del contatto, interlocutore, check Chiamate, Notifiche, Mail e check Principale.",
        "Telefono e Cellulare accettano solo numeri e richiedono il Prefisso quando la tipologia lo prevede.",
        "Email e PEC devono rispettare il formato email; l'Interlocutore accetta i caratteri previsti e viene normalizzato secondo le regole dell'anagrafica.",
        "I check Chiamate, Notifiche e Mail disponibili per la tipologia contatto sono selezionabili; quelli non previsti sono disattivati e mostrati con fondo grigio come il campo Prefisso disattivato.",
        "Per un nuovo contatto i check disponibili partono attivi secondo la configurazione della tipologia.",
        "Usa + per aggiungere un contatto e X per rimuoverlo. Imposta Principale sul recapito che deve essere preferito per quella gestione."),
      section("Gestire i Documenti",
        "Aggiungi un documento dalla sezione Documenti scegliendo prima la Tipologia Documento.",
        "Non puoi inserire due volte la stessa tipologia documento nella stessa anagrafica.",
        "Il Numero Documento viene normalizzato in maiuscolo e accetta soltanto caratteri alfanumerici previsti.",
        "Ente Rilascio accetta solo lettere, numeri e spazi.",
        "Compila date e altri campi richiesti dal documento, poi controlla che i dati corrispondano all'originale.",
        "Quando disponibile puoi acquisire i dati del documento tramite fotocamera e verificare sempre il riconoscimento prima di salvare."),
      section("Privacy e Consensi",
        "Gestisci separatamente Consenso Privacy, Marketing e Profilazione.",
        "Per i nuovi clienti i consensi e le relative date seguono le obbligatorietà previste dalla configurazione corrente.",
        "Quando attivi un consenso la data viene proposta automaticamente; la data resta comunque modificabile manualmente.",
        "Prima di salvare verifica che check e date rappresentino realmente il consenso raccolto."),
      section("Preferenze, esigenze, alert e tassa soggiorno",
        "Usa Preferenze Soggiorno per memorizzare le preferenze che devono essere riproposte nelle prenotazioni.",
        "Compila Esigenze Ospite per allergie, intolleranze, necessità o informazioni operative che devono essere visibili alla struttura.",
        "Usa Stato e Alert per indicare condizioni da mettere in evidenza senza alterare i dati anagrafici.",
        "Gestisci Imposta di Soggiorno con le informazioni previste dalla finestra quando il cliente ha esenzioni o condizioni particolari."),
      section("Riferimenti Esterni e Dati Aggiuntivi",
        "Nella sezione Riferimenti Esterni puoi aggiungere righe con Origine e ID Esterno per collegare il cliente a sistemi esterni.",
        "Usa + per aggiungere una riga e X per rimuoverla; evita duplicati che puntano allo stesso riferimento.",
        "Completa Dati Aggiuntivi e Note soltanto con informazioni utili e pertinenti all'operatività.",
        "Premi Salva e Chiudi. Se esistono errori, i campi vengono evidenziati e il pallino della sezione interessata diventa rosso; cliccalo per portare quella sezione in alto."),
      section("Ricerca, filtri e gestione elenco",
        "Usa ricerca rapida e filtri a imbuto per trovare l'anagrafica senza scorrere l'intera tabella.",
        "Usa Modifica sulla riga corretta e verifica sempre nome, tipo anagrafica e riferimenti prima di salvare.",
        "Dal menu Opzioni puoi azzerare filtri, importare, esportare o usare le altre funzioni disponibili in base ai permessi.",
        "Usa eliminazioni o operazioni multiple solo dopo aver controllato la selezione e l'impatto sulle prenotazioni collegate."),
    ],
  },
  "/app/prints": {
    title: "Stampe",
    intro: "Stampe raccoglie i report operativi disponibili per reception, camere, contabilità e controllo giornaliero. Ogni stampa può avere filtri propri, anteprima, stampa ed esportazione.",
    sections: [
      section("Scegliere la stampa",
        "Apri Stampe e individua il riquadro del report che ti serve.",
        "Leggi titolo e descrizione per capire quali dati vengono estratti.",
        "Apri il report e attendi il caricamento dei dati prima di applicare filtri o esportare.",
        "Se il report non è quello corretto, chiudilo e scegli un'altra tipologia senza modificare i dati sorgente."),
      section("Usare filtri e periodo",
        "Espandi i filtri disponibili per il report scelto.",
        "Imposta periodo, sede, stato o altri criteri richiesti e applicali.",
        "Controlla il numero di righe e i totali ottenuti prima di stampare.",
        "Azzera i filtri per tornare al set completo dei dati previsto dal report.",
        "Tra le stampe disponibili trovi anche soggiorni conclusi, prenotazioni cancellate, servizi addebitati, pagamenti e acconti, documenti annullati/stornati, castelletto IVA, fatturato cliente e riepilogo per tipologia documento."),
      section("Anteprima e stampa",
        "Apri l'anteprima del report e controlla intestazioni, colonne, date e totali.",
        "Usa l'orientamento previsto quando il report consente Verticale o Orizzontale.",
        "Premi Stampa soltanto dopo aver verificato l'anteprima: la stampa deve rispettare la stessa impaginazione visualizzata.",
        "Se manca un dato, correggilo nella finestra sorgente e poi rigenera la stampa."),
      section("Esportare",
        "Usa Esporta/Download per salvare il report nel formato disponibile.",
        "L'esportazione deve riflettere i filtri attivi e lo stesso contenuto dell'anteprima.",
        "Apri il file ottenuto e verifica che intestazioni e dati siano completi prima di inoltrarlo."),
    ],
  },
  "/app/statistics": {
    title: "Statistiche",
    intro: "Statistiche contiene estrazioni per analizzare andamento, occupazione, ospiti e operatività. I risultati dipendono dal periodo e dai filtri impostati.",
    sections: [
      section("Scegliere l'analisi",
        "Apri Statistiche e seleziona la tipologia di analisi che risponde alla domanda che vuoi verificare.",
        "Leggi la descrizione del riquadro prima di aprirlo per evitare di confrontare indicatori diversi.",
        "Attendi il caricamento completo dei dati prima di interpretare valori o percentuali."),
      section("Impostare periodo e filtri",
        "Imposta il periodo corretto e gli eventuali filtri per sede, tipologia, stato o altre dimensioni disponibili.",
        "Applica i filtri e verifica che il periodo mostrato nel risultato corrisponda a quello richiesto.",
        "Quando confronti due analisi usa criteri omogenei, altrimenti i numeri non sono confrontabili."),
      section("Leggere i risultati",
        "Controlla intestazioni, unità di misura e totali prima di trarre conclusioni.",
        "Distingui valori assoluti, percentuali e medie.",
        "Se un dato sembra anomalo, verifica prima prenotazioni, soggiorni o camere che lo alimentano.",
        "Ripeti l'estrazione dopo eventuali correzioni dei dati sorgente.",
        "Le analisi aggiuntive includono lead time, trend mensile, IVA per aliquota, servizi per tipologia, durata soggiorni, clienti singoli/ricorrenti, andamento documenti fiscali e incidenza cancellazioni."),
      section("Stampare o esportare",
        "Apri l'anteprima quando disponibile e controlla che filtri e periodo siano visibili o comunque coerenti con l'estrazione.",
        "Usa Stampa per produrre il report cartaceo e Esporta/Download per il file.",
        "Conserva insieme al file l'informazione sul periodo e sui filtri usati per poter ripetere l'analisi."),
    ],
  },
  "/app/profitability": {
    title: "Redditività",
    intro: "Redditività raccoglie analisi economiche su ricavi, margini, canali e performance. Per ottenere confronti utili è fondamentale usare periodi e filtri coerenti.",
    sections: [
      section("Scegliere l'indicatore economico",
        "Apri Redditività e scegli il riquadro che rappresenta l'analisi economica desiderata.",
        "Leggi la descrizione per capire se stai analizzando ricavo, margine, canale, camera o un altro indicatore.",
        "Non confrontare indicatori diversi come se misurassero la stessa cosa."),
      section("Applicare filtri corretti",
        "Imposta il periodo da analizzare e gli altri filtri disponibili.",
        "Per confronti tra periodi usa la stessa durata e gli stessi criteri quando possibile.",
        "Applica i filtri e verifica sempre il contesto prima di leggere i totali."),
      section("Interpretare ricavi e margini",
        "Leggi prima il totale generale e poi il dettaglio per categoria o dimensione.",
        "Controlla eventuali valori anomali tornando ai soggiorni e ai documenti che generano i ricavi.",
        "Ricorda che una modifica dei dati operativi può cambiare le analisi successive, quindi riesegui il report dopo le correzioni.",
        "Sono disponibili anche ticket medio soggiorno, incidenza extra, valore cliente, mix documenti fiscali, margine extra, ricavo per notte, concentrazione del fatturato e riepilogo imponibile/IVA/lordo."),
      section("Stampare, esportare e condividere",
        "Verifica l'anteprima prima di stampare o esportare.",
        "Usa il formato disponibile più adatto alla condivisione o all'analisi esterna.",
        "Prima di inoltrare il file ricontrolla periodo, filtri, intestazioni e totali."),
    ],
  },
  "/app/sites": {
    title: "Sedi",
    intro: "Sedi gestisce le strutture operative e legali, i loro recapiti e i Default Sede che precompilano molte funzioni di Clienti, Planning, servizi e fatturazione.",
    sections: [
      section("Cercare e gestire le sedi",
        "Usa ricerca e filtri di colonna per trovare la sede corretta.",
        "Controlla Descrizione, Natura Sede, stato e ordine prima di aprire Modifica.",
        "Usa Ordine soltanto con il livello di abilitazione necessario.",
        "Dal menu Opzioni puoi importare, esportare, azzerare filtri o usare le altre operazioni abilitate."),
      section("Dati Principali",
        "Premi Nuova Sede oppure Modifica.",
        "Compila Descrizione e Natura Sede, entrambi secondo le obbligatorietà indicate nell'overlay.",
        "Scegli con attenzione la natura perché distingue il ruolo della sede nel gestionale, per esempio operativa o legale quando previsto.",
        "Correggi eventuali errori usando il pallino rosso della sezione prima di salvare."),
      section("Ubicazione e Recapiti",
        "Compila Indirizzo e Civico.",
        "Seleziona Località dal catalogo e poi il CAP coerente con la località scelta.",
        "Telefono accetta solo numeri; Mail deve rispettare il formato email.",
        "Rileggi indirizzo e recapiti perché possono essere utilizzati nei documenti e nelle comunicazioni della struttura."),
      section("Default Sede: cliente e fatturazione",
        "Apri la sezione Default Sede e il gruppo Cliente e Fatturazione.",
        "Imposta il tipo anagrafica proposto per il nuovo Intestatario Fattura e gli altri default presenti nel gruppo.",
        "Usa i default per velocizzare l'inserimento senza rendere obbligatorio un valore che non deve esserlo.",
        "Dopo una modifica crea una prenotazione di prova per verificare che il valore venga proposto correttamente."),
      section("Default Sede: prenotazioni e contatti",
        "Configura lo stato prenotazione predefinito e l'intervallo giorni predefinito tra check in e check out quando disponibili.",
        "Configura tipo contatto e prefisso predefiniti usati nelle nuove anagrafiche.",
        "Controlla i default obbligatori o precompilati per le preferenze soggiorno secondo le opzioni presenti nella sezione.",
        "Verifica il risultato aprendo una nuova anagrafica o prenotazione dopo il salvataggio."),
      section("Servizi Default della sede",
        "Nel gruppo dei servizi default seleziona un servizio dal catalogo e premi + per aggiungerlo.",
        "I servizi configurati qui possono essere addebitati automaticamente alle prenotazioni In House secondo la logica prevista.",
        "Rimuovi con X soltanto i servizi che non devono più essere applicati come default.",
        "Controlla in Soggiorni che l'Origine Inserimento del servizio generato sia indicata come Default."),
      section("Salvare, ordinare e verificare",
        "Premi Salva e Chiudi dopo aver controllato tutte le sezioni.",
        "Se una sezione contiene errori il relativo pallino diventa rosso e ti porta direttamente alla sezione.",
        "Dopo il salvataggio verifica i default nelle finestre che li utilizzano, invece di dare per scontato che una configurazione sia corretta."),
    ],
  },
  "/app/users": {
    title: "Utenti",
    intro: "Utenti gestisce le credenziali e il profilo di accesso alla webapp, compresi username, nome completo, email, ruolo, password, stato, ordine e sblocco del login.",
    sections: [
      section("Cercare gli utenti",
        "Usa ricerca rapida e filtri di colonna per Nome Completo, Username, Email, Ruolo, ultimo accesso e altri campi disponibili.",
        "Controlla la riga corretta prima di premere Modifica.",
        "Usa i filtri per distinguere rapidamente utenti attivi, ruoli o account che non accedono da tempo quando i campi sono disponibili."),
      section("Creare un nuovo utente",
        "Premi Nuovo Utente.",
        "Nella sezione Dati Accesso compila Username e Nome Completo.",
        "Inserisci Email se prevista e seleziona obbligatoriamente il Ruolo corretto.",
        "Imposta la Password richiesta per il nuovo account e verifica i requisiti previsti dalla validazione.",
        "Premi Salva e Chiudi e verifica che l'utente compaia nell'elenco."),
      section("Modificare ruolo e dati di accesso",
        "Apri Modifica sulla riga interessata.",
        "Aggiorna Nome Completo, Email o Ruolo soltanto quando necessario.",
        "Ricorda che il Ruolo determina le abilitazioni alle pagine, comprese Preventivi, Fatture e Fatturazione, oltre a Tabelle e Sistema.",
        "Se modifichi il ruolo di un utente già collegato, le nuove autorizzazioni si applicano secondo la gestione sessione prevista dall'applicazione."),
      section("Password e sblocco login",
        "Apri Modifica Utente quando devi cambiare la password o sbloccare un accesso.",
        "Usa il campo Password secondo le regole previste per nuovo utente o modifica.",
        "Usa Sblocca Login soltanto quando devi rimuovere il blocco dell'account generato dai tentativi falliti o dalla logica di sicurezza.",
        "Salva e fai verificare l'accesso all'utente senza condividere credenziali in chiaro oltre il necessario."),
      section("Stato, ordine, importazione ed esportazione",
        "Disattiva un utente quando non deve più accedere ma vuoi conservarne lo storico.",
        "Usa Ordine con frecce o valore numerico solo con permesso adeguato.",
        "Importa più utenti soltanto con un file conforme e controlla attentamente ruolo e password richiesti.",
        "Usa Esporta per ottenere l'elenco e Eliminazione Multipla solo quando l'eliminazione è realmente appropriata."),
    ],
  },
  "/app/roles": {
    title: "Ruoli",
    intro: "Ruoli definisce cosa può vedere e modificare ogni profilo tramite livelli di autorizzazione sulle pagine, sulle Tabelle e sulle collection di Sistema.",
    sections: [
      section("Capire i livelli di autorizzazione",
        "Apri Ruoli e scegli il ruolo da consultare o modificare.",
        "I livelli disponibili vanno interpretati secondo la legenda dell'applicazione: un livello più alto abilita operazioni più avanzate rispetto al solo accesso.",
        "Non aumentare un livello solo per risolvere un problema momentaneo: assegna il minimo livello necessario al lavoro reale dell'utente.",
        "Ricorda che un utente eredita le autorizzazioni dal ruolo assegnato in Utenti."),
      section("Dati Ruolo",
        "Premi Nuovo Ruolo oppure Modifica.",
        "Compila la descrizione del ruolo in modo riconoscibile e coerente con la funzione aziendale.",
        "Controlla stato e ordine del ruolo nella lista quando disponibili.",
        "Passa poi alle sezioni dei permessi prima di salvare."),
      section("Permessi Pagine",
        "Apri Permessi Pagine e scorri tutte le finestre disponibili nello stesso ordine logico della sidebar.",
        "Imposta per ogni pagina il livello corretto, distinguendo accesso, modifica e funzioni amministrative secondo la scala prevista.",
        "Controlla in particolare Planning, Soggiorni, Clienti, Azienda, Utenti e Ruoli perché possono contenere dati o operazioni sensibili.",
        "Salva e verifica con un utente di prova che la sidebar mostri soltanto ciò che il ruolo deve poter usare."),
      section("Permessi Tabelle",
        "Apri Permessi Tabelle per assegnare i livelli sui cataloghi gestiti dalla finestra Tabelle.",
        "Un utente può avere accesso alla pagina Tabelle ma non necessariamente lo stesso livello su ogni catalogo.",
        "Imposta i livelli in modo coerente con creazione, modifica, ordine, attivazione e cancellazione dei valori."),
      section("Permessi Sistema",
        "Apri Permessi Sistema per configurare le collection tecniche esposte dalla finestra Sistema.",
        "Concedi livelli elevati soltanto ai ruoli che devono realmente amministrare dati tecnici.",
        "Dopo il salvataggio verifica la collection con un utente del ruolo interessato per assicurarti che lettura e azioni siano corrette."),
      section("Modificare un ruolo già assegnato",
        "Prima di cambiare un ruolo già in uso valuta quali utenti ne saranno interessati.",
        "Modifica soltanto i permessi necessari e salva.",
        "Controlla gli utenti collegati e verifica l'effetto reale aprendo le finestre interessate con quel livello di autorizzazione."),
    ],
  },
  "/app/tables": {
    title: "Tabelle",
    intro: "Tabelle gestisce i cataloghi configurabili usati dalle tendine e dalle regole operative della webapp. Da qui puoi cercare, creare, modificare, ordinare, attivare, importare ed esportare i valori consentiti.",
    sections: [
      section("Scegliere il catalogo",
        "Apri Tabelle e seleziona il gruppo o catalogo dalla tendina principale.",
        "Leggi titolo, descrizione e colonne del catalogo prima di modificare un valore.",
        "Usa ricerca rapida e filtri a imbuto per restringere l'elenco.",
        "Azzera ricerca e filtri dal menu Opzioni quando vuoi tornare alla vista completa."),
      section("Aggiungere un valore",
        "Premi Nuovo Valore per il catalogo corrente.",
        "Compila Descrizione e gli eventuali campi aggiuntivi richiesti dal catalogo.",
        "Rimuovi gli spazi superflui e usa una descrizione coerente con i valori esistenti.",
        "Premi Salva e Chiudi e verifica che il nuovo valore compaia nella posizione prevista."),
      section("Modificare, attivare o disattivare",
        "Premi Modifica sulla riga corretta.",
        "Correggi soltanto il valore necessario senza cambiare il significato di record già usati nello storico.",
        "Preferisci Disattiva quando un valore non deve più essere proposto ma deve restare valido per dati storici.",
        "Riattiva un valore solo dopo aver verificato che sia ancora corretto e non duplicato."),
      section("Ordine e larghezza colonne",
        "Usa le frecce o il campo Ordine per stabilire la posizione dei valori nelle tendine quando hai il permesso richiesto.",
        "Le colonne regolabili partono con una larghezza sufficiente a mostrare il titolo completo e possono essere ridimensionate secondo le funzioni della tabella.",
        "Dopo aver cambiato Ordine apri una tendina che usa quel catalogo per verificare il risultato."),
      section("Importare, esportare ed eliminare",
        "Usa Importa Valori solo con file compatibili con il catalogo corrente.",
        "Controlla duplicati, campi obbligatori, stato e ordine prima della conferma.",
        "Usa Esporta Valori per creare una copia consultabile dell'elenco filtrato o completo secondo la funzione disponibile.",
        "Usa Eliminazione Multipla soltanto dopo aver verificato che i record non siano necessari allo storico o ad altre funzioni."),
      section("Aprire un valore dalla ricerca globale",
        "Quando arrivi dalla Ricerca globale attendi Caricamento Valore Selezionato e non intervenire nel frattempo.",
        "La webapp seleziona il catalogo corretto, applica il testo cercato e carica il record anche se non era nella prima tranche visibile.",
        "Il valore trovato viene evidenziato in oro per renderlo riconoscibile.",
        "Prima di modificarlo controlla comunque catalogo, descrizione e permessi del ruolo."),
    ],
  },
  "/app/notifications": {
    title: "Notifiche",
    intro: "Notifiche è il centro operativo degli avvisi personali e dell’hotel. Mostra priorità e stato letto, controlla arrivi, check-out, soggiorni, camere, manutenzioni, preventivi e documenti fiscali e collega direttamente alla finestra da gestire.",
    sections: [
      section("Leggere il riepilogo",
        "I riquadri iniziali mostrano Totali, Da leggere e Prioritarie.",
        "La campanella in Topbar mostra il numero delle notifiche non lette e si aggiorna automaticamente durante l’uso della webapp.",
        "Gli avvisi con priorità Attenzione o Urgente vanno controllati prima degli avvisi informativi."),
      section("Cercare e filtrare",
        "Usa Cerca nelle notifiche per cercare nel titolo, nel messaggio e nella categoria.",
        "Filtra per stato Letta/Da leggere, per priorità e per categoria operativa.",
        "Usa Azzera filtri per tornare alla vista completa e Aggiorna per forzare immediatamente il ricalcolo degli avvisi."),
      section("Gestire letto e non letto",
        "Premi Segna letta quando hai preso in carico un avviso oppure Segna da leggere se vuoi riportarlo tra quelli da seguire.",
        "Lo stato è persistente per il tuo utente e resta coerente anche cambiando dispositivo o ricaricando la webapp.",
        "Segna tutte lette aggiorna in un’unica operazione tutti gli avvisi attualmente disponibili per il tuo utente."),
      section("Aprire la finestra collegata",
        "Premi Apri per raggiungere la finestra di origine; l’avviso viene marcato come letto prima della navigazione.",
        "La modifica del dato deve essere eseguita nella finestra di origine, non nel testo della notifica.",
        "Gli avvisi operativi si riallineano ai dati reali: per esempio un preventivo chiuso non resta segnalato come scaduto aperto."),
      section("Avvisi automatici disponibili",
        "Arrivi e check-in possono evidenziare documenti ospite mancanti; i check-out possono richiamare saldi aperti o soggiorni da completare.",
        "Camere sporche, da controllare, in manutenzione o fuori servizio e manutenzioni attive vengono riportate con la relativa priorità.",
        "I preventivi Bozza prossimi alla scadenza o già scaduti vengono segnalati automaticamente.",
        "I documenti fiscali recenti senza email o telefono completi vengono richiamati per facilitare condivisione tramite Mail, WhatsApp o Messaggio."),
      section("Ordine di lavoro consigliato",
        "Gestisci prima Urgente e Attenzione, soprattutto check-in, check-out, camere fuori servizio e manutenzioni.",
        "Poi controlla soggiorni, preventivi in scadenza e documenti fiscali con recapiti incompleti.",
        "Usa le notifiche come cruscotto di controllo: prima di correggere verifica sempre il record sorgente aperto dal pulsante Apri."),
    ],
  },
  "/app/services": {
    title: "Servizi",
    intro: "Servizi configura il catalogo degli addebiti utilizzabili nelle prenotazioni e nei soggiorni, con descrizione, tariffa, quantità predefinita, tipo addebito, aliquota IVA, stato e ordine.",
    sections: [
      section("Cercare e leggere i servizi",
        "Usa ricerca rapida e filtri di colonna per trovare il servizio corretto.",
        "Controlla Descrizione, Prezzo/Tariffa, Quantità, Tipo Addebito, % IVA, Totale Imponibile, Totale Imposta e Totale Ivato quando presenti nella tabella.",
        "Verifica stato e ordine perché influenzano disponibilità e posizione del servizio nelle tendine operative."),
      section("Creare o modificare un servizio",
        "Premi Nuovo Servizio oppure Modifica sulla riga interessata.",
        "Nella sezione Dati Servizio compila descrizione, tariffa, quantità predefinita, aliquota IVA, tipo addebito e gli altri campi presenti.",
        "Scegli il tipo addebito corretto, per esempio Forfait, Giornaliero, Orario o Accumulo secondo la funzione del servizio.",
        "Controlla i calcoli proposti prima di salvare e premi Salva e Chiudi."),
      section("IVA e utilizzo nei soggiorni",
        "L'aliquota IVA salvata nel servizio viene proposta quando il servizio viene addebitato a una prenotazione.",
        "Nella tab Servizi del Soggiorno controlla sempre % Aliquota IVA e Totale Ivato prima di salvare l'addebito.",
        "Una modifica al catalogo non deve essere usata per alterare retroattivamente un documento fiscale già emesso."),
      section("Default, stato e ordine",
        "I servizi attivi possono essere selezionati nei nuovi addebiti secondo le regole previste.",
        "Disattiva un servizio che non deve più essere proposto ma deve rimanere nello storico.",
        "Modifica Ordine solo con il permesso necessario e verifica la nuova posizione nelle tendine.",
        "Per i servizi da applicare automaticamente alle prenotazioni In House usa la configurazione Servizi Default nella finestra Sedi."),
      section("Importazione, esportazione ed eliminazione",
        "Usa Importa per caricare più servizi rispettando campi, formati e valori IVA previsti.",
        "Usa Esporta per controllare o archiviare il catalogo corrente.",
        "Usa Eliminazione Multipla solo dopo aver verificato l'impatto sui dati storici; quando possibile preferisci Disattiva."),
    ],
  },
  "/app/company": {
    title: "Azienda",
    intro: "Azienda contiene il profilo societario e fiscale, il logo e una lista di conti bancari. Questi dati vengono riutilizzati nella Topbar, nei documenti fiscali e nelle altre funzioni che necessitano dell'identità aziendale.",
    sections: [
      section("Logo Aziendale",
        "Apri Azienda e usa la sezione Logo Aziendale per controllare l'immagine corrente.",
        "Premi Carica Logo e scegli un file PNG, JPG/JPEG o WEBP nei limiti previsti.",
        "Usa trascinamento e zoom disponibili nell'anteprima per posizionare il logo nel riquadro senza modificarne il file sorgente.",
        "Premi Elimina Logo solo se vuoi rimuoverlo dal profilo aziendale.",
        "Verifica Topbar e anteprima dei documenti fiscali dopo la modifica."),
      section("Dati Principali e Fiscali",
        "Compila Ragione Sociale e gli altri campi obbligatori indicati con asterisco.",
        "Controlla Forma Giuridica, Partita IVA, Codice Fiscale, Codice SDI/Destinatario, Regime Fiscale, EORI e gli altri identificativi mostrati nella sezione.",
        "Rileggi i dati prima di salvare perché vengono utilizzati nell'intestazione di Fatture e Ricevute.",
        "L'indirizzo della sede legale resta gestito nella finestra Sedi e viene unito ai dati aziendali nel documento fiscale."),
      section("Dati Societari",
        "Compila REA, Registro Imprese, Capitale Sociale, Numero Soci, Stato Liquidazione e gli altri campi societari disponibili.",
        "Usa i valori previsti dalle tendine senza creare varianti testuali non necessarie.",
        "Controlla in particolare i dati destinati ai documenti fiscali prima di premere Salva."),
      section("Aggiungere un conto in Dati Bancari",
        "Premi il pulsante + nella lista Dati Bancari. Il pulsante usa lo stesso aspetto del + delle liste negli overlay.",
        "Senza espandere il gruppo puoi vedere i primi otto campi: ABI, CAB, Conto Corrente, IBAN, CIN, Località, CAP e Indirizzo Succursale.",
        "Inserendo un IBAN italiano valido vengono ricavati i componenti disponibili e, quando ABI/CAB sono presenti nel catalogo, vengono caricati i dati della succursale.",
        "Puoi anche selezionare ABI e CAB e lasciare che la webapp completi banca, succursale e dati disponibili.",
        "Se inserisci ABI, CAB e numero conto, il sistema può calcolare l'IBAN italiano quando tutti i dati necessari sono validi."),
      section("Espandere e completare un conto bancario",
        "Usa il pulsante di espansione della riga per mostrare i campi aggiuntivi.",
        "Dopo i primi otto campi trovi Codice SIA, BIC / SWIFT, Tipo Rapporto e il check Principale nell'ordine previsto.",
        "Codice SIA e BIC / SWIFT vengono normalizzati secondo lunghezza e caratteri ammessi.",
        "Seleziona Tipo Rapporto dalla tendina disponibile.",
        "Il check Principale ha lo stesso riquadro e lo stesso stile dei check delle liste overlay. Se lo attivi su un conto, gli altri conti vengono deselezionati come principali.",
        "Nel gruppo Prenotazione e Documento Fiscale imposta Aliquota IVA default: viene usata solo come fallback quando camera, servizio o riga documento hanno IVA mancante o pari a zero.",
        "Usa X per rimuovere soltanto la riga bancaria che non deve più essere salvata."),
      section("Salvare o annullare le modifiche",
        "Premi Salva per registrare i dati aziendali e bancari dopo aver controllato tutte le sezioni.",
        "Se un campo è errato o incompleto correggilo prima del salvataggio.",
        "Usa Azzera Modifiche per ripristinare i dati salvati quando vuoi scartare variazioni non ancora confermate.",
        "Il logo usa i propri comandi di caricamento e rimozione e può aggiornarsi separatamente dai campi testuali."),
    ],
  },
  "/app/system": {
    title: "Sistema",
    intro: "Sistema espone le collection tecniche amministrabili dal ruolo corrente. È una finestra avanzata: modifica soltanto record e campi di cui conosci l'effetto sulle altre funzioni della webapp.",
    sections: [
      section("Scegliere collection e gruppo",
        "Apri Sistema e usa l'elenco o la tendina di selezione per scegliere gruppo e collection.",
        "Leggi nome e descrizione della collection prima di operare.",
        "Se una collection non è visibile, controlla i Permessi Sistema del ruolo invece di cercare scorciatoie.",
        "Mantieni selezionata una sola collection per volta mentre esegui modifiche.",
        "Le collection di sistema con prefisso sys_ sono raccolte qui; tra queste è disponibile anche sys_bank per banche e succursali."),
      section("Cercare e filtrare record",
        "Usa ricerca rapida e filtri a imbuto sulle colonne per trovare il record corretto.",
        "Azzera ricerca e filtri quando vuoi tornare alla collection completa.",
        "Quando arrivi dalla Ricerca globale attendi il caricamento del valore selezionato e controlla l'evidenziazione oro prima di modificarlo."),
      section("Creare o modificare un valore",
        "Usa Nuovo Valore soltanto quando la collection ammette la creazione manuale.",
        "Compila i campi mostrati nell'overlay rispettando tipo, formato e obbligatorietà.",
        "Per un valore esistente premi Modifica e cambia soltanto i campi necessari.",
        "Se il valore è usato nello storico valuta Disattiva invece dell'eliminazione."),
      section("Stato, ordine ed eliminazione",
        "Gestisci Attivo e Ordine solo se la collection prevede questi campi e il tuo ruolo ha il livello necessario.",
        "Usa le frecce o il campo Ordine senza creare valori incoerenti nella sequenza.",
        "Elimina o usa Eliminazione Multipla soltanto dopo aver verificato le dipendenze del record."),
      section("Importare ed esportare",
        "Esporta la collection prima di modifiche estese quando vuoi avere un riferimento consultabile.",
        "Importa soltanto file compatibili con i campi della collection selezionata.",
        "Dopo l'importazione controlla ricerca, stato, ordine e almeno una finestra che utilizza quei dati per verificare l'effetto reale."),
    ],
  },
};

function getHelpForPath(pathname) {
  return HELP_CONTENT[pathname] || {
    title: "Finestra",
    intro: "Questa guida spiega come orientarti nella finestra corrente, individuare i dati corretti, usare filtri e azioni e salvare senza perdere il contesto.",
    sections: [
      section("Orientarsi nella finestra", "Leggi titolo e descrizione della finestra.", "Usa ricerca e filtri per trovare il record corretto.", "Controlla sempre la riga o il dato selezionato prima di aprire una modifica."),
      section("Modificare e salvare", "Apri l'overlay dalla relativa azione.", "Compila i campi obbligatori e correggi quelli evidenziati.", "Usa i pallini laterali per passare rapidamente tra le sezioni dell'overlay.", "Premi Salva e Chiudi e verifica il risultato nella finestra di partenza."),
    ],
  };
}

function normalizeSearch(value) {
  return String(value || "").trim().toLocaleLowerCase("it-IT");
}

function sectionMatches(sectionItem, query) {
  const token = normalizeSearch(query);
  if (!token) return false;
  return [sectionItem.title, ...(sectionItem.steps || [])]
    .some((value) => normalizeSearch(value).includes(token));
}

function highlightSearchText(value, query) {
  const text = String(value || "");
  const token = normalizeSearch(query);
  if (!token) return text;

  const normalizedText = text.toLocaleLowerCase("it-IT");
  const parts = [];
  let cursor = 0;
  let matchIndex = normalizedText.indexOf(token, cursor);

  while (matchIndex >= 0) {
    if (matchIndex > cursor) parts.push(text.slice(cursor, matchIndex));
    const matchEnd = matchIndex + token.length;
    parts.push(<mark className="window_help_search_match" key={`${matchIndex}-${matchEnd}`}>{text.slice(matchIndex, matchEnd)}</mark>);
    cursor = matchEnd;
    matchIndex = normalizedText.indexOf(token, cursor);
  }

  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts.length ? parts : text;
}

export default function AppWindowHelp() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);
  const help = useMemo(() => getHelpForPath(location.pathname), [location.pathname]);
  const normalizedSearch = normalizeSearch(searchQuery);
  const matchingSections = useMemo(
    () => help.sections.map((sectionItem) => sectionMatches(sectionItem, normalizedSearch)),
    [help.sections, normalizedSearch],
  );
  const matchingCount = normalizedSearch ? matchingSections.filter(Boolean).length : help.sections.length;
  const allExpanded = help.sections.length > 0 && help.sections.every((_, index) => Boolean(expandedSections[index]));

  const [bannerElement, setBannerElement] = useState(null);
  const previousBannerRef = useRef(null);

  useEffect(() => {
    function syncBanner() {
      const nextBanner = document.querySelector(".app_content .window_banner");
      setBannerElement((current) => (current === nextBanner ? current : nextBanner));
    }

    syncBanner();
    const observer = new MutationObserver(syncBanner);
    const target = document.querySelector(".app_content") || document.body;
    observer.observe(target, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [location.pathname]);

  useEffect(() => {
    if (previousBannerRef.current && previousBannerRef.current !== bannerElement) {
      previousBannerRef.current.classList.remove("window_banner_has_help");
    }

    if (bannerElement) {
      bannerElement.classList.add("window_banner_has_help");
      previousBannerRef.current = bannerElement;
    }

    return () => {
      if (bannerElement) bannerElement.classList.remove("window_banner_has_help");
    };
  }, [bannerElement]);

  useEffect(() => {
    setOpen(false);
    setExpandedSections({});
    setSearchQuery("");
  }, [location.pathname]);

  useEffect(() => {
    const openHelpFromKeyboard = (event) => {
      if (event.key !== "F1" || event.ctrlKey || event.metaKey || event.altKey) return;
      event.preventDefault();
      setOpen(true);
    };
    document.addEventListener("keydown", openHelpFromKeyboard);
    return () => document.removeEventListener("keydown", openHelpFromKeyboard);
  }, []);

  useEffect(() => {
    if (!open) return;
    setExpandedSections({});
    setSearchQuery("");
    const focusTimer = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open, help.title]);

  function toggleSection(sectionIndex) {
    setExpandedSections((current) => ({
      ...current,
      [sectionIndex]: !current[sectionIndex],
    }));
  }

  function toggleAllSections() {
    if (allExpanded) {
      setExpandedSections({});
      return;
    }
    setExpandedSections(help.sections.reduce((acc, _, index) => ({ ...acc, [index]: true }), {}));
  }

  const helpButton = bannerElement ? createPortal(
    <button
      type="button"
      className="window_help_button"
      onClick={() => setOpen(true)}
      aria-label={`Manuale finestra ${help.title}`}
      title="Apri Manuale di Utilizzo (F1)"
    >
      ?
    </button>,
    bannerElement,
  ) : null;

  const overlay = open ? (
    <div className="overlay_backdrop window_help_backdrop" role="dialog" aria-modal="true" aria-label={`Manuale di Utilizzo - ${help.title}`}>
      <div className="overlay_card window_help_panel">
        <header className="overlay_header window_help_header">
          <div className="window_help_header_text">
            <h2>Manuale di Utilizzo - {help.title}</h2>
          </div>
          <button type="button" className="overlay_close_button window_help_close" onClick={() => setOpen(false)} aria-label="Chiudi manuale">×</button>
        </header>

        <div className="overlay_body window_help_body">
          <p className="window_help_intro">{highlightSearchText(help.intro, normalizedSearch)}</p>
          <div className="window_help_search_wrap">
            <input
              ref={searchInputRef}
              type="search"
              className="window_help_search_input"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Cerca nel manuale..."
              aria-label="Cerca nel manuale"
              autoComplete="off"
            />
            <div className="window_help_search_tools">
              <span className="window_help_search_status">{normalizedSearch ? `${matchingCount} sezioni trovate` : `${help.sections.length} argomenti`}</span>
              <button type="button" className="window_help_control_button" onClick={toggleAllSections}>{allExpanded ? "Comprimi tutto" : "Espandi tutto"}</button>
              {searchQuery && <button type="button" className="window_help_control_button" onClick={() => setSearchQuery("")}>Azzera ricerca</button>}
            </div>
          </div>
          <div className="window_help_sections">
            {normalizedSearch && matchingCount === 0 && <div className="window_help_empty_search">Nessun argomento corrisponde alla ricerca.</div>}
            {help.sections.map((sectionItem, sectionIndex) => {
              const matchesSearch = Boolean(normalizedSearch && matchingSections[sectionIndex]);
              if (normalizedSearch && !matchesSearch) return null;
              const isExpanded = matchesSearch || Boolean(expandedSections[sectionIndex]);
              const panelId = `window-help-section-${sectionIndex}`;
              return (
                <section
                  key={sectionItem.title}
                  className={`window_help_section${isExpanded ? " is_expanded" : ""}${matchesSearch ? " is_search_match" : ""}`}
                  data-overlay-search-match={matchesSearch ? "true" : "false"}
                >
                  <button
                    type="button"
                    className="window_help_section_toggle"
                    onClick={() => toggleSection(sectionIndex)}
                    aria-expanded={isExpanded}
                    aria-controls={panelId}
                  >
                    <span className="window_help_section_number">{String(sectionIndex + 1).padStart(2, "0")}</span>
                    <span className="window_help_section_title">{highlightSearchText(sectionItem.title, normalizedSearch)}</span>
                    <span className="window_help_expand_button" aria-hidden="true">
                      <span className="window_help_expand_icon"><span /><span /></span>
                    </span>
                  </button>
                  {isExpanded && (
                    <div id={panelId} className="window_help_section_body">
                      <ol className="window_help_steps">
                        {sectionItem.steps.map((step, stepIndex) => (
                          <li key={`${sectionItem.title}-${stepIndex}`}>{highlightSearchText(step, normalizedSearch)}</li>
                        ))}
                      </ol>
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        </div>

        <footer className="overlay_footer window_help_footer">
          <button type="button" className="overlay_save_button window_help_footer_button" onClick={() => setOpen(false)}>Chiudi Manuale</button>
        </footer>
      </div>
    </div>
  ) : null;

  return (
    <>
      {helpButton}
      {overlay}
    </>
  );
}
